import { useCallback, useEffect, useRef, useState } from 'react';
import { throttle } from 'throttle-debounce';

import { QueryParameter } from '@/shared/constants/queryParams';
import { useOnFullscreenChange } from '@/scenarios/legacy-player/components/PlayerSkin/parts/Layout/hooks/useOnFullscreenChange';
import { usePlayingState } from '@/scenarios/legacy-player/hooks/usePlayingState';
import { PlayingState } from '@/scenarios/legacy-player/types/instance';
import { useSearchParams } from '@/shared/hooks/useSearchParams';

const HIDE_CONTROLS_TIMEOUT = 3000;
const SHOW_CONTROLS_TIMEOUT = 1000;
const MOUSE_MOTION_THROTTLE = 300;

export const useControlsVisibility = (isAlwaysVisible: boolean = false) => {
    const controlsVisibilityCancellationTimer = useRef<number>();
    const { playingState } = usePlayingState();
    const [isControlsVisible, setIsControlsVisible] = useState<boolean>(true);
    const [isHidingControlsBlocked, setIsHidingControlsBlocked] = useState<boolean>(false);
    const [isShowingControlsBlocked, setIsShowingControlsBlocked] = useState<boolean>(false);

    const keepShowingPlayerControls = useSearchParams().has(
        QueryParameter.KEEP_SHOWING_PLAYER_CONTROLS
    );

    const isPlaying =
        playingState && [PlayingState.play, PlayingState.buffering].includes(playingState);

    const clearTimer = useCallback(() => {
        if (controlsVisibilityCancellationTimer.current) {
            window.clearTimeout(controlsVisibilityCancellationTimer.current);
            controlsVisibilityCancellationTimer.current = undefined;
        }
    }, []);

    const blockShowingControls = useCallback(() => {
        setIsShowingControlsBlocked(true);
        const timeout = window.setTimeout(
            () => setIsShowingControlsBlocked(false),
            SHOW_CONTROLS_TIMEOUT
        );

        return () => window.clearTimeout(timeout);
    }, []);

    const isControlsHideEnabled =
        !isHidingControlsBlocked && !isAlwaysVisible && !keepShowingPlayerControls;

    const startClosingTimer = useCallback(() => {
        clearTimer();
        controlsVisibilityCancellationTimer.current = window.setTimeout(() => {
            if (isPlaying && isControlsHideEnabled) {
                setIsControlsVisible(false);
            }
            controlsVisibilityCancellationTimer.current = undefined;
        }, HIDE_CONTROLS_TIMEOUT);
    }, [clearTimer, isPlaying, isControlsHideEnabled]);

    const showControls = useCallback(() => {
        if (!isShowingControlsBlocked) {
            clearTimer();
            setIsControlsVisible(true);
        }
    }, [clearTimer, isShowingControlsBlocked]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onMouseMove = useCallback(
        throttle(MOUSE_MOTION_THROTTLE, true, () => {
            showControls();
            startClosingTimer();
        }),
        [startClosingTimer, showControls]
    );

    useEffect(() => {
        if (isPlaying && isControlsHideEnabled) {
            startClosingTimer();
        } else {
            showControls();
        }
    }, [startClosingTimer, isPlaying, showControls, isControlsHideEnabled]);

    const onFullscreenChange = useCallback(
        (isFullscreen: boolean) => {
            if (isPlaying && isFullscreen && isControlsHideEnabled) {
                clearTimer();
                setIsControlsVisible(false);
                blockShowingControls();
            }
        },
        [blockShowingControls, clearTimer, isControlsHideEnabled, isPlaying]
    );

    useOnFullscreenChange(onFullscreenChange);

    return {
        isControlsVisible,
        initiateControlsVisibility: onMouseMove,
        setIsHidingControlsBlocked,
    };
};
